import React from 'react';
import PropTypes from 'prop-types';
import {Button} from 'react-bootstrap';
import {FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';

import withIntl from '../../lib/withIntl';

import './About.scss';
import {LicenseStatus, UserRoleLevel} from '../../constants/dataEnum';

const licenseStatus = {
  NOT_ACTIVATED: 'Not Activated',
  MAINTENANCE_ACTIVATED: 'Maintenance Activated',
  MAINTENANCE_EXPIRED: 'Maintenance Expired',
  TEMPORARY_ACTIVATED: 'Temporary Activated',
  TEMPORARY_EXPIRED: 'License Expired',
};

function AboutItem({
  label,
  value,
  isLicense,
  isSoftwareUpdater,
  info,
  prevVersion,
  tooltip,
  showDowngradeModal,
  showUpdateAvailable,
  userRoleLevel,
  isDowngradable,
  subItem,
}) {
  return (
    <div className="about__content__item">
      <span
        className={`about__content__item__label
        ${isLicense ? 'isLicense' : ''}
        ${isSoftwareUpdater ? 'isSoftwareUpdater' : ''}
        ${subItem ? 'subItem' : ''}`}
      >
        {label}
      </span>
      <span
        className={`about__content__item__value
          ${
            value === licenseStatus.MAINTENANCE_ACTIVATED || value === licenseStatus.TEMPORARY_ACTIVATED
              ? 'license-active'
              : ''
          }
          ${
            value === licenseStatus.NOT_ACTIVATED ||
            value === licenseStatus.MAINTENANCE_EXPIRED ||
            value === licenseStatus.TEMPORARY_EXPIRED
              ? 'license-inactive'
              : ''
          }
          ${prevVersion && userRoleLevel && isDowngradable ? 'prevVersion' : ''}
          ${prevVersion && userRoleLevel && !isDowngradable ? 'greyout' : ''}
          ${showUpdateAvailable && userRoleLevel ? 'updateAvailable' : ''}`}
        onClick={() => {
          if (userRoleLevel) showDowngradeModal();
          if (userRoleLevel && showUpdateAvailable) {
            showUpdateAvailable('', false);
          }
        }}
        role="presentation"
      >
        {value || '-'}
      </span>
      {(prevVersion || showUpdateAvailable) && userRoleLevel && (
        <div className="about__content__item__icon ">
          <FmlxTooltip title={tooltip} placement="bottom">
            <span>
              <FmlxIcon
                size={FmlxIcon.Size.SMALL}
                name="Info"
                customColor={`${showUpdateAvailable ? '#4caf50' : '#0059cc'}`}
              />
            </span>
          </FmlxTooltip>
        </div>
      )}
      <span className="about__content__item__info">
        {info}
        {!!showUpdateAvailable}
      </span>
    </div>
  );
}

AboutItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  info: PropTypes.string,
  tooltip: PropTypes.string,
  isLicense: PropTypes.bool,
  isSoftwareUpdater: PropTypes.bool,
  prevVersion: PropTypes.bool,
  userRoleLevel: PropTypes.bool,
  isDowngradable: PropTypes.bool,
  subItem: PropTypes.bool,
  showDowngradeModal: PropTypes.func,
  showUpdateAvailable: PropTypes.any,
};

AboutItem.defaultProps = {
  value: '',
  info: '',
  tooltip: '',
  isLicense: false,
  isSoftwareUpdater: false,
  prevVersion: false,
  userRoleLevel: false,
  isDowngradable: false,
  subItem: false,
  showDowngradeModal: () => {},
  showUpdateAvailable: null,
};

function About({
  intl,
  logoIconSrc,
  logoIconAlt,
  logoTextSrc,
  logoTextAlt,
  logoTessara,
  logoTessaraAlt,
  showSoftwareUpdateModal,
  showDowngradeModal,
  showActivateLicenseModal,
  data,
  licenseData,
  userRoleLevel,
  isTessaraInstrument,
}) {
  // eslint-disable-next-line consistent-return
  const getLicenseStatus = (stat) => {
    switch (stat) {
      case LicenseStatus.NO_LICENSE: {
        return licenseStatus.NOT_ACTIVATED;
      }
      case LicenseStatus.PERMANENT: {
        return licenseStatus.MAINTENANCE_ACTIVATED;
      }
      case LicenseStatus.PERMANENT_EXPIRED: {
        return licenseStatus.MAINTENANCE_EXPIRED;
      }
      case LicenseStatus.TRIAL:
      case LicenseStatus.TEMPORARY: {
        return licenseStatus.TEMPORARY_ACTIVATED;
      }
      case LicenseStatus.TRIAL_EXPIRED: {
        return licenseStatus.TEMPORARY_EXPIRED;
      }
      default:
        return licenseStatus.NOT_ACTIVATED;
    }
  };

  const systemInformationItems = [
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.deviceSerialNumber.label'}),
      value: data.deviceSerialNumber,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.instrumentControlVersion.label'}),
      value: data.instrumentControlVersion,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.firmwareVersion.label'}),
      value: ' ',
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.firmwareHead.label'}),
      value: data.firmwareHeadVersion,
      subItem: true,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.firmwareChasis.label'}),
      value: data.firmwareChassisVersion,
      subItem: true,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.firmwareWeightScale.label'}),
      value: data.firmwareWeightScaleVersion,
      subItem: true,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.tipPlungerUsageCycle.label'}),
      value: data.tipPlungerUsageCycle,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.visionVersion.label'}),
      value: data.visionVersion,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.labwareVersion.label'}),
      value: data.labwareVersion,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.systemInformation.ipAddress.label'}),
      value: data.IPAddress,
    },
  ];

  const licenseItems = [
    {
      label: intl.formatMessage({id: 'about.aboutContent.license.status.label'}),
      value: getLicenseStatus(licenseData.status),
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.license.modifiedDate.label'}),
      value: licenseData.modifiedDate ? new Date(licenseData.modifiedDate).toLocaleDateString() : '-',
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.license.expiredDate.label'}),
      value: licenseData.expiredDate ? new Date(licenseData.expiredDate).toLocaleDateString() : '-',
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.license.clientId.label'}),
      value: licenseData.clientID,
    },
  ];

  const softwareUpdateItem = [
    {
      label: intl.formatMessage({id: 'about.aboutContent.softwareUpdate.currentVersion.label'}),
      value: data.softwareVersion,
      info:
        intl.formatMessage({id: 'about.aboutContent.softwareUpdate.installedOn.label'}) +
        (data.softwareInstalledDate ? new Date(data.softwareInstalledDate).toLocaleDateString() : ''),
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.softwareUpdate.releaseDate.label'}),
      value: data.softwareReleaseDate ? new Date(data.softwareReleaseDate).toLocaleDateString() : '',
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.softwareUpdate.previousVersion.label'}),
      value: data.isDowngradable
        ? data.prevSWVersion
        : intl.formatMessage({id: 'about.aboutContent.softwareUpdate.previousVersion.unableToDowngrade.value'}),
      info: data.isDowngradable
        ? intl.formatMessage({id: 'about.aboutContent.softwareUpdate.installedOn.label'}) +
          (data.prevSWInstalledDate ? new Date(data.prevSWInstalledDate).toLocaleDateString() : '')
        : '',
      prevVersion: !!data.prevSWVersion,
      tooltip: data.isDowngradable
        ? intl.formatMessage({id: 'about.aboutContent.softwareUpdate.icon.tooltip'})
        : intl.formatMessage({id: 'about.aboutContent.softwareUpdate.icon.unableToDowngrade.tooltip'}),
      showDowngradeModal: data.isDowngradable ? showDowngradeModal : () => {},
      isDowngradable: data.isDowngradable,
    },
    {
      label: intl.formatMessage({id: 'about.aboutContent.softwareUpdate.releaseDate.label'}),
      value: data.prevSWReleaseDate ? new Date(data.prevSWReleaseDate).toLocaleDateString() : '',
    },
  ];

  const disableUpdateLicenseButton = userRoleLevel === UserRoleLevel.OPERATOR;
  const disableUpdateSoftwareButton =
    userRoleLevel === UserRoleLevel.USER ||
    userRoleLevel === UserRoleLevel.OPERATOR ||
    licenseData.status === LicenseStatus.NO_LICENSE ||
    licenseData.status === LicenseStatus.TRIAL_EXPIRED ||
    licenseData.status === LicenseStatus.TEMPORARY_EXPIRED;

  return (
    <div className="about">
      <div className="about__wrapper">
        <div className="about__brand">
          <img src={logoIconSrc} alt={logoIconAlt} style={{margin: '0 10px 0 5px'}} />
          <img src={logoTextSrc} alt={logoTextAlt} />
        </div>
        <div className="about__content">
          <div className="about__content__wrapper">
            <div className="about__content__title">
              {intl.formatMessage({id: 'about.aboutContent.systemInformation.title'})}
            </div>
            <div className="about__content__subtitle">
              {intl.formatMessage({id: 'about.aboutContent.systemInformation.subtitle'})}
            </div>

            {systemInformationItems
              .filter((e) => e.prevVersion !== false)
              .map((item) => (
                <AboutItem key={item.label} label={item.label} value={item.value} subItem={item.subItem} />
              ))}
          </div>

          <div className="about__content__wrapper">
            <div className="about__content__title">{intl.formatMessage({id: 'about.aboutContent.license.title'})}</div>
            <div className="about__content__subtitle">
              {intl.formatMessage({id: 'about.aboutContent.license.subtitle'})}
            </div>

            {licenseItems.map((item) => (
              <AboutItem key={item.label} label={item.label} value={item.value} isLicense />
            ))}
            <div className="about__button__tooltip__container">
              <Button
                className="about__button button-secondary"
                disabled={disableUpdateLicenseButton}
                onClick={() => showActivateLicenseModal(true)}
              >
                {licenseData.status === LicenseStatus.NO_LICENSE
                  ? intl.formatMessage({id: 'about.aboutContent.license.activateLicense.button'})
                  : intl.formatMessage({id: 'about.aboutContent.license.updateLicense.button'})}
              </Button>
              {disableUpdateLicenseButton && (
                <FmlxTooltip
                  title={intl.formatMessage({id: 'about.aboutContent.license.updateLicenseDisabled.tooltip'})}
                >
                  <span>
                    <FmlxIcon size={FmlxIcon.Size.SMALL} name="Info" customColor="#0059CC" />
                  </span>
                </FmlxTooltip>
              )}
            </div>
          </div>

          <div className="about__content__wrapper">
            <div className="about__content__title">
              {intl.formatMessage({id: 'about.aboutContent.softwareUpdate.title'})}
            </div>
            <div className="about__content__subtitle">
              {intl.formatMessage({id: 'about.aboutContent.softwareUpdate.subtitle'})}
            </div>

            {softwareUpdateItem.map((item, idx) => {
              const showUpdateAvailable =
                item.showUpdateAvailable &&
                userRoleLevel !== UserRoleLevel.ADMINISTRATOR &&
                userRoleLevel !== UserRoleLevel.SERVICE;

              return !showUpdateAvailable ? (
                <AboutItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={item.label + idx}
                  label={item.label}
                  value={item.value}
                  info={item.info}
                  prevVersion={item.prevVersion}
                  tooltip={item.tooltip}
                  showDowngradeModal={item.showDowngradeModal}
                  isDowngradable={item.isDowngradable}
                  showUpdateAvailable={showUpdateAvailable}
                  userRoleLevel={
                    userRoleLevel === UserRoleLevel.ADMINISTRATOR ||
                    userRoleLevel === UserRoleLevel.SERVICE ||
                    userRoleLevel === UserRoleLevel.TESSARA_SUPER_USER
                  }
                  isSoftwareUpdater
                />
              ) : null;
            })}

            <div className="about__button__tooltip__container">
              <Button
                className="about__button button-secondary"
                onClick={showSoftwareUpdateModal}
                disabled={disableUpdateSoftwareButton}
              >
                {intl.formatMessage({id: 'about.aboutContent.systemInformation.updateSoftware.button'})}
              </Button>
              {disableUpdateSoftwareButton && (
                <FmlxTooltip
                  title={intl.formatMessage({id: 'about.aboutContent.softwareUpdate.updateSoftwareDisabled.tooltip'})}
                >
                  <span>
                    <FmlxIcon size={FmlxIcon.Size.SMALL} name="Info" customColor="#0059CC" />
                  </span>
                </FmlxTooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="about__footer">
        <span className="about__footer__text-left">
          {isTessaraInstrument && <img src={logoTessara} alt={logoTessaraAlt} style={{margin: '0 10px 0 5px'}} />}
        </span>
        <span className="about__footer__text-center">{intl.formatMessage({id: 'about.aboutFooter.center.text'})}</span>
        <span className="about__footer__text-right">
          &copy; &nbsp;
          {new Date(data?.softwareReleaseDate).getFullYear()}
          &nbsp;
          {intl.formatMessage({id: 'about.aboutFooter.right.text'})}
        </span>
      </div>
    </div>
  );
}

About.propTypes = {
  intl: PropTypes.any.isRequired,
  logoIconSrc: PropTypes.string,
  logoIconAlt: PropTypes.string,
  logoTextSrc: PropTypes.string,
  logoTextAlt: PropTypes.string,
  logoTessara: PropTypes.string,
  logoTessaraAlt: PropTypes.string,
  showSoftwareUpdateModal: PropTypes.func,
  showDowngradeModal: PropTypes.func,
  showActivateLicenseModal: PropTypes.func,
  data: PropTypes.object.isRequired,
  licenseData: PropTypes.object.isRequired,
  userRoleLevel: PropTypes.number,
  isTessaraInstrument: PropTypes.bool,
};

About.defaultProps = {
  logoIconSrc: './svg/login/fast-icon.svg',
  logoIconAlt: 'FAST Icon',
  logoTextSrc: './svg/login/fast-text.svg',
  logoTextAlt: 'Fast Liquid Handler',
  logoTessara: './svg/login/tessara.svg',
  logoTessaraAlt: 'Tessara',
  showSoftwareUpdateModal: () => {},
  showDowngradeModal: () => {},
  showActivateLicenseModal: () => {},
  userRoleLevel: 0,
  isTessaraInstrument: false,
};

export default withIntl(About);
